import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { Toaster } from '@/components/ui/sonner';
import CookieConsent from './components/CookieConsent';
import ScrollToTop from './components/ScrollToTop';
import RouteSeo from './components/RouteSeo';

// Layouts
import { PublicLayout } from './components/layout/PublicLayout';

// Public Pages
import HomePage from './pages/public/HomePage';
const ServicesPage = lazy(() => import('./pages/public/ServicesPage'));
const SoftwarePage = lazy(() => import('./pages/public/SoftwarePage'));
const SoftwareDetailPage = lazy(() => import('./pages/public/SoftwareDetailPage'));
const ReferencesPage = lazy(() => import('./pages/public/ReferencesPage'));
const ContactPage = lazy(() => import('./pages/public/ContactPage'));
const AboutPage = lazy(() => import('./pages/public/AboutPage'));
const LegalPage = lazy(() => import('./pages/public/LegalPage'));
const FAQPage = lazy(() => import('./pages/public/FAQPage'));
const AccessibilityPage = lazy(() => import('./pages/public/AccessibilityPage'));
const CancellationPage = lazy(() => import('./pages/public/CancellationPage'));
const WebsiteAuditPage = lazy(() => import('./pages/public/WebsiteAuditPage'));
const ToolsPage = lazy(() => import('./pages/public/ToolsPage'));
const StatusPage = lazy(() => import('./pages/public/StatusPage'));
const GameServerRentalPage = lazy(() => import('./pages/public/GameServerRentalPage'));
const NotFoundPage = lazy(() => import('./pages/public/NotFoundPage'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage'));
const PortalLayout = lazy(() => import('./components/layout/PortalLayout').then(module => ({ default: module.PortalLayout })));
const AdminLayout = lazy(() => import('./components/layout/AdminLayout').then(module => ({ default: module.AdminLayout })));
const PortalDashboard = lazy(() => import('./pages/portal/PortalDashboard'));
const PortalTickets = lazy(() => import('./pages/portal/PortalTickets'));
const NewTicketPage = lazy(() => import('./pages/portal/NewTicketPage'));
const TicketDetailPage = lazy(() => import('./pages/portal/TicketDetailPage'));
const PortalProfile = lazy(() => import('./pages/portal/PortalProfile'));
const PortalDownloads = lazy(() => import('./pages/portal/PortalDownloads'));
const PortalInvoices = lazy(() => import('./pages/portal/PortalInvoices'));
const PortalOffers = lazy(() => import('./pages/portal/PortalOffers'));
const PortalReviews = lazy(() => import('./pages/portal/PortalReviews'));
const PortalContracts = lazy(() => import('./pages/portal/PortalContracts'));
const PortalGameServers = lazy(() => import('./pages/portal/PortalGameServers'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminTickets = lazy(() => import('./pages/admin/AdminTickets'));
const AdminTicketDetail = lazy(() => import('./pages/admin/AdminTicketDetail'));
const AdminPages = lazy(() => import('./pages/admin/AdminPages'));
const AdminMedia = lazy(() => import('./pages/admin/AdminMedia'));
const AdminSoftware = lazy(() => import('./pages/admin/AdminSoftware'));
const AdminReferences = lazy(() => import('./pages/admin/AdminReferences'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminAuditLog = lazy(() => import('./pages/admin/AdminAuditLog'));
const AdminSystem = lazy(() => import('./pages/admin/AdminSystem'));
const AdminMenus = lazy(() => import('./pages/admin/AdminMenus'));
const AdminForms = lazy(() => import('./pages/admin/AdminForms'));
const AdminRedirects = lazy(() => import('./pages/admin/AdminRedirects'));
const AdminCompanyAssets = lazy(() => import('./pages/admin/AdminCompanyAssets'));
const QuickEdit = lazy(() => import('./pages/admin/QuickEdit'));
const HomepageEditor = lazy(() => import('./pages/admin/HomepageEditor'));
const LegalPageEditor = lazy(() => import('./pages/admin/LegalPageEditor'));
const CompanyDataEditor = lazy(() => import('./pages/admin/CompanyDataEditor'));
const NavigationEditor = lazy(() => import('./pages/admin/NavigationEditor'));
const SetupWizard = lazy(() => import('./pages/admin/SetupWizard'));
const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics'));
const PageEditor = lazy(() => import('./pages/admin/PageEditor'));
const ContentInstaller = lazy(() => import('./pages/admin/ContentInstaller'));
const BrandingEditor = lazy(() => import('./pages/admin/BrandingEditor'));
const AdminServices = lazy(() => import('./pages/admin/AdminServices'));
const AdminContracts = lazy(() => import('./pages/admin/AdminContracts'));
const AdminFAQs = lazy(() => import('./pages/admin/AdminFAQs'));
const WebsiteHub = lazy(() => import('./pages/admin/WebsiteHub'));
const KundenHub = lazy(() => import('./pages/admin/KundenHub'));
const EinstellungenHub = lazy(() => import('./pages/admin/EinstellungenHub'));
const PaymentSettings = lazy(() => import('./pages/admin/PaymentSettings'));
const AdminAccounting = lazy(() => import('./pages/admin/AdminAccounting'));
const AdminWebsiteAudits = lazy(() => import('./pages/admin/AdminWebsiteAudits'));
const ForumHome = lazy(() => import('./pages/forum/ForumHome'));
const ForumBoardPage = lazy(() => import('./pages/forum/ForumBoardPage'));
const ForumThreadPage = lazy(() => import('./pages/forum/ForumThreadPage'));
const ForumMe = lazy(() => import('./pages/forum/ForumMe'));
const ForumProfilePage = lazy(() => import('./pages/forum/ForumProfilePage'));
const AdminForum = lazy(() => import('./pages/admin/forum/AdminForum'));
const AdminGameServers = lazy(() => import('./pages/admin/AdminGameServers'));

// Analytics hook
import { useAnalytics } from './lib/analytics';

// Analytics wrapper component
function AnalyticsWrapper({ children }) {
    useAnalytics();
    return children;
}

function App() {
    return (
        <AuthProvider>
            <LanguageProvider>
                <BrowserRouter>
                    <AnalyticsWrapper>
                        <RouteSeo />
                        <ScrollToTop />
                        <Suspense fallback={<div className="min-h-screen bg-[#0b0d10]" role="status" aria-label="Seite wird geladen" />}>
                        <Routes>
                            {/* Auth Routes (no layout) */}
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/register" element={<RegisterPage />} />
                        <Route path="/verify-email" element={<VerifyEmailPage />} />
                        
                        {/* Public Routes */}
                        <Route element={<PublicLayout />}>
                            <Route path="/" element={<HomePage />} />
                            <Route path="/leistungen" element={<ServicesPage />} />
                            <Route path="/leistungen/:slug" element={<ServicesPage />} />
                            <Route path="/software" element={<SoftwarePage />} />
                            <Route path="/software/:slug" element={<SoftwareDetailPage />} />
                            <Route path="/referenzen" element={<ReferencesPage />} />
                            <Route path="/referenzen/:slug" element={<ReferencesPage />} />
                            <Route path="/ueber-uns" element={<AboutPage />} />
                            <Route path="/kontakt" element={<ContactPage />} />
                            <Route path="/impressum" element={<LegalPage />} />
                            <Route path="/datenschutz" element={<LegalPage />} />
                            <Route path="/agb" element={<LegalPage />} />
                            <Route path="/widerruf" element={<LegalPage />} />
                            <Route path="/faq" element={<FAQPage />} />
                            <Route path="/barrierefreiheit" element={<AccessibilityPage />} />
                            <Route path="/kuendigung" element={<CancellationPage />} />
                            <Route path="/website-check" element={<WebsiteAuditPage />} />
                            <Route path="/tools" element={<ToolsPage />} />
                            <Route path="/tools/:toolSlug" element={<ToolsPage />} />
                            <Route path="/status" element={<StatusPage />} />
                            <Route path="/gameserver-leihen" element={<GameServerRentalPage />} />
                            <Route path="/forum" element={<ForumHome />} />
                            <Route path="/forum/me" element={<ForumMe />} />
                            <Route path="/forum/u/:username" element={<ForumProfilePage />} />
                            <Route path="/forum/:boardSlug" element={<ForumBoardPage />} />
                            <Route path="/forum/t/:threadSlug" element={<ForumThreadPage />} />
                            <Route path="*" element={<NotFoundPage />} />
                        </Route>
                        
                        {/* Portal Routes */}
                        <Route path="/portal" element={<PortalLayout />}>
                            <Route index element={<PortalDashboard />} />
                            <Route path="tickets" element={<PortalTickets />} />
                            <Route path="tickets/new" element={<NewTicketPage />} />
                            <Route path="tickets/:ticketId" element={<TicketDetailPage />} />
                            <Route path="vertraege" element={<PortalContracts />} />
                            <Route path="angebote" element={<PortalOffers />} />
                            <Route path="downloads" element={<PortalDownloads />} />
                            <Route path="rechnungen" element={<PortalInvoices />} />
                            <Route path="bewertungen" element={<PortalReviews />} />
                            <Route path="profile" element={<PortalProfile />} />
                            <Route path="gameserver" element={<PortalGameServers />} />
                        </Route>
                        
                        {/* Admin Routes */}
                        <Route path="/admin" element={<AdminLayout />}>
                            <Route index element={<QuickEdit />} />
                            <Route path="schnell" element={<QuickEdit />} />
                            <Route path="website" element={<WebsiteHub />} />
                            <Route path="startseite" element={<HomepageEditor />} />
                            <Route path="rechtliches/:type" element={<LegalPageEditor />} />
                            <Route path="firmendaten" element={<CompanyDataEditor />} />
                            <Route path="zahlungsdaten" element={<PaymentSettings />} />
                            <Route path="navigation" element={<NavigationEditor />} />
                            <Route path="branding" element={<BrandingEditor />} />
                            <Route path="setup" element={<SetupWizard />} />
                            <Route path="einrichtung" element={<SetupWizard />} />
                            <Route path="analytics" element={<AdminAnalytics />} />
                            <Route path="website-audits" element={<AdminWebsiteAudits />} />
                            <Route path="seiten" element={<AdminPages />} />
                            <Route path="seiten/neu" element={<PageEditor />} />
                            <Route path="seiten/:id/bearbeiten" element={<PageEditor />} />
                            <Route path="medien" element={<AdminMedia />} />
                            <Route path="menues" element={<AdminMenus />} />
                            <Route path="formulare" element={<AdminForms />} />
                            <Route path="redirects" element={<AdminRedirects />} />
                            <Route path="software" element={<AdminSoftware />} />
                            <Route path="referenzen" element={<AdminReferences />} />
                            <Route path="kundenassets" element={<AdminCompanyAssets />} />
                            <Route path="content-installer" element={<ContentInstaller />} />
                            <Route path="leistungen" element={<AdminServices />} />
                            <Route path="kunden" element={<KundenHub />} />
                            <Route path="vertraege" element={<AdminContracts />} />
                            <Route path="rechnungswesen" element={<AdminAccounting />} />
                            <Route path="forum" element={<AdminForum />} />
                            <Route path="gameserver" element={<AdminGameServers />} />
                            <Route path="faqs" element={<AdminFAQs />} />
                            <Route path="tickets" element={<AdminTickets />} />
                            <Route path="tickets/:ticketId" element={<AdminTicketDetail />} />
                            <Route path="benutzer" element={<AdminUsers />} />
                            <Route path="einstellungen" element={<EinstellungenHub />} />
                            <Route path="protokoll" element={<AdminAuditLog />} />
                            <Route path="system" element={<AdminSystem />} />
                            <Route path="dashboard" element={<AdminDashboard />} />
                        </Route>
                        
                        </Routes>
                        </Suspense>
                        <CookieConsent />
                    </AnalyticsWrapper>
                </BrowserRouter>
                <Toaster position="top-right" />
            </LanguageProvider>
        </AuthProvider>
    );
}

export default App;
